import { useState, useEffect } from "react";
import { CHARACTERS, LEVELS_META, hasNearbyUnlock } from "@/game/config";
import { CharacterPortrait } from "@/components/CharacterPortrait";
import { MenuBackdrop } from "@/components/MenuBackdrop";
import { useLang } from "@/i18n";
import { useAuth } from "@/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Lock, Lightbulb, Star, ExternalLink, Sword, Music, Mountain, Hand, Dumbbell, Zap, ChevronsUp, Utensils, Beer, CircleDot, Coins, Sparkles, Croissant, Droplets, Wind, ArrowLeft, RotateCw } from "lucide-react";

// Short action label per special ability (Spanish — matches in-game content).
const ACT_LABEL = {
  nacatamal: "Lanza Nacatamales", fritanga: "Lanza Fritangas", throw: "Lanza Botellas",
  ball: "Patea Balones", notes: "Notas Musicales", rock: "Lanza Piedras", coconut: "Lanza Cocos", electric: "Rayos Eléctricos", bread: "Lanza Pan", cream: "Exprime Crema", tornado: "Lanza Tornados", homerun: "Batea un Jonrón",
  machete: "Machetazo", chop: "Karatazo", strength: "Pisotón Poderoso", speed: "Dash Veloz", hypno: "Puño Hipnótico",
  maraca: "Lanza Maracas", spin: "Giro Veloz",
};
const ACT_ICON = {
  nacatamal: Utensils, fritanga: Utensils, throw: Beer, ball: CircleDot, notes: Music, coconut: CircleDot,
  rock: Mountain, machete: Sword, chop: Hand, strength: Dumbbell, speed: Zap, homerun: CircleDot,
  electric: Zap, bread: Croissant, cream: Droplets, tornado: Wind, starnote: Star, hypno: Sparkles,
  maraca: Music, spin: RotateCw, headbutt: ChevronsUp,
};

function buildPerks(c, t) {
  const perks = [];
  perks.push({ Icon: ACT_ICON[c.ability] || Zap, label: c.actLabelKey ? t(c.actLabelKey, c.actLabel || "") : (c.actLabel || t(`act_${c.ability}`, ACT_LABEL[c.ability] || c.power)), color: c.colors.accent });
  if (c.jumps >= 3) perks.push({ Icon: ChevronsUp, label: t("perkTripleJump", "Triple Salto"), color: "#7e57c2" });
  else if (c.jumps === 2) perks.push({ Icon: ChevronsUp, label: t("perkDoubleJump", "Doble Salto"), color: "#0067C6" });
  if (c.speed >= 4.6) perks.push({ Icon: Zap, label: t("perkSuperFast", "Súper Rápido"), color: "#FF9800" });
  else if (c.speed >= 4.2) perks.push({ Icon: Zap, label: t("perkFast", "Veloz"), color: "#FF9800" });
  return perks;
}

export default function CharacterSelect({ onSelect, onBack, onSuggest, onCollection, mode = "normal", daily }) {
  const { t } = useLang();
  const { user, refreshUser } = useAuth();
  const isAdmin = user?.role === "admin";
  const coins = user?.coins || 0;
  const unlockedChars = user?.unlocked_chars || [];
  const [unlocking, setUnlocking] = useState(null);
  const [skins, setSkins] = useState(() => { try { return JSON.parse(localStorage.getItem("nicabros_skins") || "{}"); } catch { return {}; } });
  const variantOf = (c) => c.variants ? (c.variants.find((v) => v.key === skins[c.id]) || c.variants[0]) : null;
  const withSkin = (c) => { const v = variantOf(c); return v ? { ...c, spriteId: v.sprite, ability: v.ability || c.ability, multiShot: v.multiShot, ballKind: v.ballKind, actLabel: v.actLabel, actLabelKey: v.actLabelKey, powerKey: v.powerKey, power: v.power || c.power, powText: v.powText || c.powText } : c; };
  const pickSkin = (c, key) => { const next = { ...skins, [c.id]: key }; setSkins(next); localStorage.setItem("nicabros_skins", JSON.stringify(next)); };
  // Refresh profile on open so admin-granted characters unlock without a full re-login.
  useEffect(() => { if (user) refreshUser(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const handleUnlock = async (c) => {
    if (!user) { toast.error(t("signInToUnlock")); return; }
    if (coins < c.coinCost) { toast.error(t("notEnoughCoins")); return; }
    setUnlocking(c.id);
    try {
      await api.unlockCharacter(c.id);
      await refreshUser();
      toast.success(t("charUnlocked")(c.name));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("notEnoughCoins"));
    }
    setUnlocking(null);
  };
  const dailyLevel = daily ? LEVELS_META[daily.levelIndex] : null;
  const diffLabel = daily ? t(`diff${daily.diffId.charAt(0).toUpperCase() + daily.diffId.slice(1)}`) : "";

  const invitedOrder = { chasedawson: 0, gordankemash: 1, dongregorio: 2 };
  const invited = CHARACTERS.filter((c) => c.invited).sort((a, b) => (invitedOrder[a.id] ?? 9) - (invitedOrder[b.id] ?? 9));
  const guests = CHARACTERS.filter((c) => c.guest && !c.invited);
  const cultural = CHARACTERS.filter((c) => c.cultural && !c.guest && !c.food);
  const food = CHARACTERS.filter((c) => c.food && !c.guest);
  const originals = CHARACTERS.filter((c) => !c.guest && !c.cultural && !c.food);

  const renderCard = (c) => {
    const comingSoon = !!c.comingSoon;
    const locked = (c.coinCost && !isAdmin && !unlockedChars.includes(c.id)) || comingSoon;
    const ec = withSkin(c);
    return (
      <button
        key={c.id}
        data-testid={`char-card-${c.id}`}
        onClick={() => { if (!locked) onSelect(withSkin(c)); }}
        disabled={locked}
        aria-disabled={locked}
        className={`relative w-full max-w-[264px] pixel-card bg-white text-black p-1.5 sm:p-2.5 flex flex-col items-center transition-transform ${locked ? "cursor-not-allowed" : "hover:-translate-y-1"}`}
      >
        <div className={`w-full flex justify-center ${locked ? "opacity-40 grayscale" : ""}`}>
          <div className="w-full max-w-[86px] sm:max-w-[132px] flex justify-center">
            <CharacterPortrait character={withSkin(c)} size={128} />
          </div>
        </div>
        {c.variants && !locked && (
          <div data-testid={`char-skin-toggle-${c.id}`} className="flex gap-1 mt-1.5 sm:mt-2 w-full max-w-[220px]" onClick={(e) => e.stopPropagation()}>
            {c.variants.map((v) => {
              const active = (variantOf(c).key === v.key);
              return (
                <div
                  key={v.key}
                  role="button"
                  tabIndex={0}
                  data-testid={`char-skin-${c.id}-${v.key}`}
                  onClick={(e) => { e.stopPropagation(); pickSkin(c, v.key); }}
                  className={`flex-1 font-pixel text-[7px] sm:text-[10px] px-1 py-1 sm:py-1.5 rounded-md text-center cursor-pointer transition-colors ${active ? "text-white" : "bg-gray-200 text-gray-600 hover:bg-gray-300"}`}
                  style={active ? { backgroundColor: c.colors.accent } : undefined}
                >
                  {v.label}
                </div>
              );
            })}
          </div>
        )}
        <h3 className={`font-pixel text-[10px] sm:text-sm mt-2 sm:mt-2 text-center leading-tight ${locked ? "opacity-50" : ""}`} style={{ color: c.nameColor || c.colors.accent }}>{c.name}</h3>
        <p className={`font-vt text-[11px] sm:text-sm text-gray-700 mt-0.5 sm:mt-0.5 text-center leading-tight ${locked ? "opacity-50" : ""}`}>{t(`tag_${c.id}`, c.tagline)}</p>
        {c.link && !locked && (
          <a
            href={c.link.url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => { e.stopPropagation(); api.trackCharLink({ char_id: c.id, name: c.name, label: c.link.label, url: c.link.url }); }}
            data-testid={`char-link-${c.id}`}
            className="font-vt text-[10px] sm:text-xs text-[#0067C6] underline mt-0.5 inline-flex items-center gap-1 hover:text-[#FF1744] text-center leading-tight"
          >
            <ExternalLink size={11} strokeWidth={2.5} className="shrink-0" /> {t(`link_${c.id}`, c.link.label)}
          </a>
        )}
        {comingSoon ? (
          <div data-testid={`char-comingsoon-${c.id}`} className="font-pixel text-[8px] sm:text-[10px] text-black px-2 py-1.5 sm:px-2.5 sm:py-1.5 mt-1.5 sm:mt-2 rounded-lg bg-[#FFD54F] flex items-center gap-1 text-center">
            <Sparkles size={11} strokeWidth={3} /> {t("comingSoon", "¡PRÓXIMAMENTE!")}
          </div>
        ) : locked ? (
          <div
            role="button"
            tabIndex={0}
            data-testid={`char-unlock-${c.id}`}
            onClick={(e) => { e.stopPropagation(); handleUnlock(c); }}
            className="font-pixel text-[8px] sm:text-[10px] text-black px-2 py-1.5 sm:px-2.5 sm:py-1.5 mt-1.5 sm:mt-2 rounded-lg bg-[#FFB300] flex items-center gap-1 sm:gap-1.5 hover:brightness-110 cursor-pointer text-center"
          >
            <Lock size={11} strokeWidth={3} /> {unlocking === c.id ? "…" : t("unlockForCoins")(c.coinCost)}
          </div>
        ) : (
          <div className="font-pixel text-[8px] sm:text-[10px] text-white px-2 py-1.5 sm:px-2.5 sm:py-1.5 mt-1.5 sm:mt-2 rounded-lg text-center" style={{ backgroundColor: c.colors.accent }}>
            {c.variants ? (ec.powerKey ? t(ec.powerKey, ec.power) : ec.power) : t(`power_${c.id}`, c.power)}
          </div>
        )}
        {!comingSoon && (
        <ul className={`mt-1.5 sm:mt-2 w-full space-y-1 sm:space-y-1 ${locked ? "opacity-50" : ""}`} data-testid={`char-perks-${c.id}`}>
          {buildPerks(ec, t).map((pk, i) => (
            <li key={i} className="flex items-center gap-1 sm:gap-1.5 font-vt text-[10px] sm:text-xs text-gray-800">
              <span className="shrink-0 w-4 h-4 sm:w-5 sm:h-5 rounded-md flex items-center justify-center text-white" style={{ backgroundColor: pk.color }}>
                <pk.Icon size={11} strokeWidth={2.5} className="sm:w-[12px] sm:h-[12px]" />
              </span>
              <span className="truncate">{pk.label}</span>
            </li>
          ))}
        </ul>
        )}
        {comingSoon ? (
          <div data-testid={`char-comingsoon-badge-${c.id}`} className="absolute top-1.5 right-1.5 sm:top-2.5 sm:right-2.5 bg-[#FFD54F] text-black font-pixel text-[7px] sm:text-[9px] px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full flex items-center gap-0.5 sm:gap-1">
            <Sparkles size={10} strokeWidth={3} /> {t("comingSoonBadge", "SOON")}
          </div>
        ) : locked && (
          <div className="absolute top-1.5 right-1.5 sm:top-3 sm:right-3 bg-black/80 text-[#FFEB3B] rounded-full p-1 sm:p-1.5" title={t("lockedChar")}>
            <Lock size={14} strokeWidth={3} />
          </div>
        )}
        {c.guest && !locked && (
          <>
            <div data-testid={`char-guest-${c.id}`} title={t("guestStar")} className="absolute top-1.5 left-1.5 bg-[#ffd400] text-black rounded-full p-1 sm:p-1.5 flex items-center justify-center shadow-md ring-1 ring-black/20">
              <Star size={12} strokeWidth={3} fill="#000" className="sm:w-[14px] sm:h-[14px]" />
            </div>
            <div data-testid={`char-new-${c.id}`} className="absolute top-1.5 right-1.5 sm:top-3 sm:right-3 bg-[#FF1744] text-white font-pixel text-[7px] sm:text-[10px] px-1.5 py-0.5 sm:px-2.5 sm:py-1.5 rounded-full flex items-center gap-0.5 sm:gap-1 animate-pulse">
              <Sparkles size={10} strokeWidth={3} /> {t("newBadge", "NEW")}
            </div>
          </>
        )}
        {c.isNew && !c.guest && !locked && (
          <div data-testid={`char-new-${c.id}`} className="absolute top-1.5 right-1.5 sm:top-3 sm:right-3 bg-[#FF1744] text-white font-pixel text-[7px] sm:text-[10px] px-1.5 py-0.5 sm:px-2.5 sm:py-1.5 rounded-full flex items-center gap-0.5 sm:gap-1 animate-pulse">
            <Sparkles size={10} strokeWidth={3} /> {t("newBadge", "NEW")}
          </div>
        )}
      </button>
    );
  };

  return (
    <div className="relative w-full h-full overflow-y-auto scanlines" data-testid="character-select">
      <MenuBackdrop themeIndex={1} />
      <div className="absolute inset-0 bg-black/70" />

      <button
        data-testid="char-back-top-btn"
        onClick={onBack}
        className="fixed top-3 right-3 z-30 pixel-btn bg-[#222] text-white px-3 py-2 text-[11px] sm:text-sm flex items-center gap-1.5"
      >
        <ArrowLeft size={15} strokeWidth={2.5} /> {t("back")}
      </button>

      <div className="relative z-10 w-full max-w-5xl mx-auto px-4 py-8 flex flex-col items-center">
        {mode === "daily" && dailyLevel && (
          <div className="mb-4 px-5 py-2 rounded-xl bg-[#FFB300] text-black text-center" data-testid="daily-banner">
            <p className="font-pixel text-[10px]">📅 {t("dailyChallenge")}</p>
            <p className="font-vt text-lg mt-0.5">{dailyLevel.name} · <span className="font-bold">{diffLabel}</span></p>
          </div>
        )}
        <h2 className="font-pixel text-xl md:text-3xl text-[#FFEB3B] pixel-stroke mb-3 text-center">{t("chooseChar")}</h2>
        <button data-testid="open-collection-btn" onClick={onCollection}
          className="collection-cta relative mb-4 pixel-btn bg-[#FFD400] text-black px-5 py-2.5 text-[11px] sm:text-sm flex items-center gap-2">
          <img src={`${process.env.PUBLIC_URL || ""}/game-assets/nicabros-logo.webp`} alt="" className="h-5 sm:h-6 w-auto object-contain" />
          {t("collectionBtn")}
        </button>
        {user && (
          <div data-testid="coin-balance" className="mb-5 px-6 py-3 rounded-2xl bg-black/55 ring-2 ring-[#FFB300]/60 font-pixel text-base md:text-lg text-[#FFB300] flex items-center gap-3">
            <Coins size={26} strokeWidth={2.5} /> {coins} {t("coins")}
            <span className="font-vt text-white/60 text-lg normal-case hidden sm:inline">· {t("earnCoinsHint")}</span>
          </div>
        )}

        {/* Special Guests */}
        <div className="w-full flex items-center gap-3 mb-4 mt-2" data-testid="section-guests">
          <span className="h-[2px] flex-1 bg-gradient-to-r from-transparent to-[#ffd400]/70" />
          <h3 className="font-pixel text-sm md:text-base text-[#ffd400] flex items-center gap-2 whitespace-nowrap">
            <Star size={16} strokeWidth={3} fill="#ffd400" /> {t("sectionGuests", "Special Guests")}
          </h3>
          <span className="h-[2px] flex-1 bg-gradient-to-l from-transparent to-[#ffd400]/70" />
        </div>
        <div className="grid grid-cols-3 gap-2 sm:gap-3 w-full justify-items-center mb-6">
          {guests.map(renderCard)}
        </div>

        {/* Cultural Characters */}
        <div className="w-full flex items-center gap-3 mb-4" data-testid="section-cultural">
          <span className="h-[2px] flex-1 bg-gradient-to-r from-transparent to-[#FFC107]/70" />
          <h3 className="font-pixel text-sm md:text-base text-[#FFC107] flex items-center gap-2 whitespace-nowrap">
            <Sparkles size={16} strokeWidth={3} /> {t("sectionCultural", "Cultural Characters")}
          </h3>
          <span className="h-[2px] flex-1 bg-gradient-to-l from-transparent to-[#FFC107]/70" />
        </div>
        <div className="grid grid-cols-3 gap-2 sm:gap-3 w-full justify-items-center mb-6">
          {cultural.map(renderCard)}
        </div>

        {/* Auténtica de Comida (food characters) */}
        <div className="w-full flex items-center gap-3 mb-4" data-testid="section-food">
          <span className="h-[2px] flex-1 bg-gradient-to-r from-transparent to-[#FF9800]/70" />
          <h3 className="font-pixel text-sm md:text-base text-[#FFB74D] flex items-center gap-2 whitespace-nowrap">
            <Utensils size={16} strokeWidth={3} /> {t("sectionFood", "Auténtica de Comida")}
          </h3>
          <span className="h-[2px] flex-1 bg-gradient-to-l from-transparent to-[#FF9800]/70" />
        </div>
        <div className="grid grid-cols-3 gap-2 sm:gap-3 w-full justify-items-center mb-6">
          {food.map(renderCard)}
        </div>

        {/* Special Invited Characters */}
        <div className="w-full flex items-center gap-3 mb-4 mt-2" data-testid="section-invited">
          <span className="h-[2px] flex-1 bg-gradient-to-r from-transparent to-[#ff7a18]/70" />
          <h3 className="font-pixel text-sm md:text-base text-[#ff7a18] flex items-center gap-2 whitespace-nowrap">
            <Star size={16} strokeWidth={3} fill="#ff7a18" /> {t("sectionInvited", "Special Invited Characters")}
          </h3>
          <span className="h-[2px] flex-1 bg-gradient-to-l from-transparent to-[#ff7a18]/70" />
        </div>
        <div className="grid grid-cols-3 gap-2 sm:gap-3 w-full justify-items-center mb-6">
          {invited.map(renderCard)}
        </div>

        {/* Original Characters */}
        <div className="w-full flex items-center gap-3 mb-4" data-testid="section-originals">
          <span className="h-[2px] flex-1 bg-gradient-to-r from-transparent to-[#0067C6]/70" />
          <h3 className="font-pixel text-sm md:text-base text-[#58b0ff] flex items-center gap-2 whitespace-nowrap">
            {t("sectionOriginals", "Original Characters")}
          </h3>
          <span className="h-[2px] flex-1 bg-gradient-to-l from-transparent to-[#0067C6]/70" />
        </div>
        <div className="grid grid-cols-3 gap-2 sm:gap-3 w-full justify-items-center">
          {originals.map(renderCard)}
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
          <button data-testid="char-back-btn" onClick={onBack} className="pixel-btn bg-[#222] text-white px-8 py-3.5 text-sm">
            {t("back")}
          </button>
          <button data-testid="char-suggest-btn" onClick={onSuggest} className="pixel-btn bg-[#4CAF50] text-white px-6 py-3.5 text-sm flex items-center gap-2">
            <Lightbulb size={16} strokeWidth={2.5} /> {t("suggestIdeas")}
          </button>
        </div>
      </div>
    </div>
  );
}
